/**
 * Resource GraphQL Mutations
 *
 * 7 mutations for universal asset management:
 * - createResource
 * - reserveResource
 * - releaseResource
 * - updateResourceStatus
 * - updateResourceCapacity
 * - recordUsage
 * - decommissionResource
 *
 * @see graphql_gateway/src/resource_api/resource_graphql.rs - Backend implementation
 */

import { gql } from '@apollo/client';
import { RESOURCE_FIELDS } from './fragments';

/** Create a new resource with backend Snowflake ID */
export const CREATE_RESOURCE = gql`
  ${RESOURCE_FIELDS}
  mutation CreateResource($tenantId: String!, $input: CreateResourceInput!) {
    createResource(tenantId: $tenantId, input: $input) {
      ...ResourceFields
    }
  }
`;

/** Reserve a resource for an actor (with duration) */
export const RESERVE_RESOURCE = gql`
  ${RESOURCE_FIELDS}
  mutation ReserveResource(
    $tenantId: String!
    $resourceId: String!
    $actorId: String!
    $durationHours: Int!
  ) {
    reserveResource(
      tenantId: $tenantId
      resourceId: $resourceId
      actorId: $actorId
      durationHours: $durationHours
    ) {
      ...ResourceFields
    }
  }
`;

/** Release a reserved resource */
export const RELEASE_RESOURCE = gql`
  ${RESOURCE_FIELDS}
  mutation ReleaseResource($tenantId: String!, $resourceId: String!) {
    releaseResource(tenantId: $tenantId, resourceId: $resourceId) {
      ...ResourceFields
    }
  }
`;

/** Update resource status */
export const UPDATE_RESOURCE_STATUS = gql`
  ${RESOURCE_FIELDS}
  mutation UpdateResourceStatus($tenantId: String!, $resourceId: String!, $status: ResourceStatus!) {
    updateResourceStatus(tenantId: $tenantId, resourceId: $resourceId, status: $status) {
      ...ResourceFields
    }
  }
`;

/** Update resource capacity */
export const UPDATE_RESOURCE_CAPACITY = gql`
  ${RESOURCE_FIELDS}
  mutation UpdateResourceCapacity($tenantId: String!, $resourceId: String!, $newCapacity: Int!) {
    updateResourceCapacity(tenantId: $tenantId, resourceId: $resourceId, newCapacity: $newCapacity) {
      ...ResourceFields
    }
  }
`;

/** Record resource usage (for analytics) */
export const RECORD_USAGE = gql`
  ${RESOURCE_FIELDS}
  mutation RecordUsage($tenantId: String!, $resourceId: String!, $durationMinutes: Int!) {
    recordUsage(tenantId: $tenantId, resourceId: $resourceId, durationMinutes: $durationMinutes) {
      ...ResourceFields
    }
  }
`;

/** Decommission a resource (end-of-life) */
export const DECOMMISSION_RESOURCE = gql`
  ${RESOURCE_FIELDS}
  mutation DecommissionResource($tenantId: String!, $resourceId: String!) {
    decommissionResource(tenantId: $tenantId, resourceId: $resourceId) {
      ...ResourceFields
    }
  }
`;
